import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, HeartHandshake, MapPin, ShieldCheck, Sparkles, Home as HomeIcon,
  Building2, Star, Truck, Users, Clock, Award, Leaf,
} from "lucide-react";
import useSEO from "@/lib/useSEO";
import api from "@/lib/api";
import { IMAGES } from "@/data/content";
import CmsImage from "@/components/CmsImage";
import LinkedText from "@/components/LinkedText";
import { useInternalLinks, stripLinks, LINK_RE } from "@/lib/internalLinks";
import { useBusiness } from "@/contexts/SettingsContext";

const ICON_LIB = {
  HeartHandshake, ShieldCheck, MapPin, Sparkles, Home: HomeIcon, Building2,
  Star, Truck, Users, Clock, Award, Leaf,
};

const FALLBACK = {
  hero_title: "A local cleaning team that",
  hero_title_highlight: "cares about details",
  hero_subtitle: "",
  hero_badge: "About Us",
  hero_image_data: "",
  hero_image_alt: "Cleaning team in uniform",
  main_eyebrow: "What we do",
  main_heading: "",
  main_body: "",
  main_image_data: "",
  main_image_alt: "",
  offerings: [
    "Move-in cleaning", "Move-out cleaning", "Pre-listing cleaning",
    "Property management cleaning", "Recurring cleaning", "Commercial cleaning",
  ],
  values_eyebrow: "How we work",
  values_heading: "Friendly. Reliable. Detailed.",
  values: [],
};

export default function About() {
  const BUSINESS = useBusiness();
  const [content, setContent] = useState(FALLBACK);
  // The stock hero photo may only show once the CMS has confirmed there is no replacement.
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const linkTargets = useInternalLinks();
  useSEO({
    title: "About P & A Cleaning | Sacramento Cleaning Team",
    description:
      "A Sacramento-area cleaning service specializing in move-in, move-out, pre-listing, property management, recurring, and commercial cleaning.",
  });

  useEffect(() => {
    api.get("/public/about", { timeout: 8000 })
      .then((r) => setContent({ ...FALLBACK, ...(r.data || {}) }))
      .catch(() => {})
      .finally(() => setCmsLoaded(true));
  }, []);

  const heroSrc = cmsLoaded ? (content.hero_image_data || IMAGES.team) : null;
  const mainHeading = content.main_heading || `Cleaning across ${BUSINESS.area}`;
  const heroSubtitle =
    content.hero_subtitle ||
    `${BUSINESS.name} is a Sacramento-area cleaning service helping families, landlords, property managers, realtors, and business owners hand over clean, welcoming spaces. Also known as Paula's Cleaning Service.`;

  return (
    <div data-testid="page-about">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
                <Sparkles className="w-3.5 h-3.5 text-[#34D399]" />
                {content.hero_badge || "About Us"}
              </span>
              <h1
                data-testid="about-hero-title"
                className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight leading-[1.05]"
              >
                {content.hero_title}{" "}
                <span className="gradient-text">{content.hero_title_highlight}</span>
              </h1>
              <p
                data-testid="about-hero-subtitle"
                className="mt-6 text-lg text-[#475569] max-w-xl"
              >
                {heroSubtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="about-cta-estimate"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-soft"
                >
                  Request an Estimate <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  data-testid="about-cta-services"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  View our services
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-card border border-white">
                <CmsImage
                  src={heroSrc}
                  alt={content.hero_image_alt || "Cleaning team"}
                  loading={!cmsLoaded}
                  testId="about-hero-image"
                  className="w-full h-[420px] lg:h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
                {content.main_eyebrow || "What we do"}
              </p>
              <h2
                data-testid="about-main-heading"
                className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]"
              >
                {mainHeading}
              </h2>
              <LinkedText
                text={content.main_body}
                testId="about-main-body"
                className="mt-4 text-[#475569] leading-relaxed whitespace-pre-line"
              />
              {content.main_image_data && (
                <img
                  src={content.main_image_data}
                  alt={content.main_image_alt || ""}
                  data-testid="about-main-image"
                  className="mt-6 rounded-2xl w-full max-w-md object-cover border border-slate-100"
                />
              )}
            </div>
            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3">
                {(content.offerings || []).map((o, i) => {
                  const plain = stripLinks(o);
                  // Plain service names that match a live approved page link automatically.
                  const auto = !new RegExp(LINK_RE.source).test(o)
                    ? linkTargets.find((t) => t.live && t.label.toLowerCase() === plain.trim().toLowerCase())
                    : null;
                  const text = auto ? `[${plain}](${auto.url})` : o;
                  return (
                    <li
                      key={`${o}-${i}`}
                      data-testid={`about-offering-${plain.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="bg-[#F8FAFC] border border-slate-100 rounded-2xl px-5 py-4 font-medium text-[#0F172A] flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4 text-[#34D399]" aria-hidden="true" />
                      <LinkedText
                        as="span"
                        text={text}
                        linkClassName="hover:text-[#1E3A8A] underline decoration-[#1E3A8A]/30 underline-offset-2 hover:decoration-[#1E3A8A]"
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
              {content.values_eyebrow || "How we work"}
            </p>
            <h2
              data-testid="about-values-heading"
              className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]"
            >
              {content.values_heading}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(content.values || []).map((v, i) => {
              const Icon = ICON_LIB[v.icon] || Sparkles;
              return (
                <div
                  key={v.id || `v-${i}`}
                  data-testid={`about-value-${v.id || `v-${i}`}`}
                  className="bg-white border border-slate-100 rounded-2xl p-6 shadow-soft"
                >
                  <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1E3A8A]/10 to-[#A78BFA]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" />
                  </span>
                  <h3 className="mt-4 font-display font-semibold text-[#0F172A]">
                    {v.label}
                  </h3>
                  <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
